import React, { useState, useMemo } from 'react';
import { useStore } from '../../../store/useStore';
import { PriceHistoryChart } from './PriceHistoryChart';
import { TradeHistoryList } from './TradeHistoryList';
import type { Resource } from '../../../types';
import { TrendingUp, TrendingDown, Activity, DollarSign, Clock } from 'lucide-react';
import { ItemIcon } from '../../common/ItemIcon';
import { Combobox } from '../../common/Combobox';

export const MarketAnalytics = () => {
    const market = useStore(state => state.market);
    const tradeHistory = useStore(state => state.tradeHistory || []);

    // Default to first item or wood
    const [selectedResourceId, setSelectedResourceId] = useState<Resource>('wood');

    const marketItem = market[selectedResourceId];

    // Sort items for dropdown
    const sortedItems = useMemo(() => {
        return Object.values(market).sort((a, b) => a.name.localeCompare(b.name));
    }, [market]);

    // Calculate Summary Stats from History
    const stats = useMemo(() => {
        let totalVolume = 0;
        let totalBuy = 0;
        let totalSell = 0;
        let distinctItems = new Set<Resource>();

        tradeHistory.forEach(item => {
            const val = item.amount * item.pricePerUnit;
            totalVolume += val;
            if (item.type === 'buy') totalBuy += val;
            else totalSell += val;
            distinctItems.add(item.itemId);
        });

        return {
            totalVolume,
            totalBuy,
            totalSell,
            distinctItems: distinctItems.size
        };
    }, [tradeHistory]);

    // Handle missing item (if selectedResource is invalid)
    if (!marketItem && sortedItems.length > 0) {
        setSelectedResourceId(sortedItems[0]!.id);
    }

    return (
        <div className="analytics-container">
            {/* Top Stats Grid */}
            <div className="analytics-stats-grid">
                <div className="stat-card volume">
                    <div className="stat-icon-bg"><Activity size={48} /></div>
                    <span className="stat-label">
                        <Activity size={14} /> Total Volume
                    </span>
                    <span className="stat-value">
                        {stats.totalVolume.toLocaleString()} <span className="stat-unit">G</span>
                    </span>
                </div>

                <div className="stat-card sales">
                    <div className="stat-icon-bg"><TrendingUp size={48} /></div>
                    <span className="stat-label">
                        <TrendingUp size={14} /> Total Sales
                    </span>
                    <span className="stat-value" style={{ color: 'var(--color-success)' }}>
                        +{stats.totalSell.toLocaleString()} <span className="stat-unit">G</span>
                    </span>
                </div>

                <div className="stat-card purchases">
                    <div className="stat-icon-bg"><TrendingDown size={48} /></div>
                    <span className="stat-label">
                        <TrendingDown size={14} /> Purchases
                    </span>
                    <span className="stat-value" style={{ color: 'var(--color-skill-mining)' }}>
                        -{stats.totalBuy.toLocaleString()} <span className="stat-unit">G</span>
                    </span>
                </div>

                <div className="stat-card items">
                    <div className="stat-icon-bg"><DollarSign size={48} /></div>
                    <span className="stat-label">
                        <DollarSign size={14} /> Traded Items
                    </span>
                    <span className="stat-value" style={{ color: '#3b82f6' }}>
                        {stats.distinctItems}
                    </span>
                </div>
            </div>

            <div className="analytics-main-grid">
                {/* Left Column: Chart & Analysis */}
                <div className="chart-panel">
                    <div className="chart-header">
                        <div className="chart-item-info">
                            {marketItem && (
                                <div className="chart-item-icon">
                                    <ItemIcon id={marketItem.id} size={32} />
                                </div>
                            )}
                            <div>
                                <h3 className="chart-title">Price History</h3>
                                <div className="current-price">
                                    Current Market Price:
                                    <span className="price-tag">
                                        {marketItem?.currentPrice} G
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Resource Selector */}
                        {/* Resource Selector */}
                        <div style={{ width: 250 }}>
                            <Combobox
                                options={useMemo(() => sortedItems.map(item => ({
                                    value: item.id,
                                    label: item.name,
                                    icon: <ItemIcon id={item.id} size={20} />
                                })), [sortedItems])}
                                value={selectedResourceId}
                                onChange={(val) => setSelectedResourceId(val as Resource)}
                                placeholder="Search resource..."
                            />
                        </div>
                    </div>

                    {/* Chart Container */}
                    <div className="chart-container">
                        <div className="grid-lines">
                            <div className="grid-line"></div>
                            <div className="grid-line"></div>
                            <div className="grid-line"></div>
                            <div className="grid-line"></div>
                            <div className="grid-line"></div>
                        </div>

                        {marketItem && (
                            <PriceHistoryChart
                                data={marketItem.history}
                                color="var(--color-primary)"
                                height={250}
                            />
                        )}
                    </div>
                </div>

                {/* Right Column: Recent Trades */}
                <div className="recent-activity-panel">
                    <div className="activity-header">
                        <h3 className="activity-title">
                            <Clock size={18} /> Recent Activity
                        </h3>
                    </div>
                    <TradeHistoryList history={tradeHistory} className="activity-list custom-scrollbar" />
                </div>
            </div>
        </div>
    );
};
